import React, { useEffect, useState, useCallback } from "react";
import {
  RefreshCw,
  Eye,
  EyeOff,
  MousePointerClick,
  CheckCircle,
  AlertCircle,
  Clock,
  Send,
  FileText,
  Download,
  ExternalLink,
  X,
} from "lucide-react";

const ACTION_LABELS = {
  quote_sent: "Quote sent",
  contract_sent: "Contract sent",
  rental_sent: "Rental sent",
  config_updated: "System Config updated",
};

const STATUS_STYLES = {
  queued:    { label: "Queued",    cls: "bg-gray-100 text-gray-700",       Icon: Clock },
  sent:      { label: "Sent",      cls: "bg-blue-100 text-blue-700",       Icon: Send },
  delivered: { label: "Delivered", cls: "bg-indigo-100 text-indigo-700",   Icon: CheckCircle },
  opened:    { label: "Opened",    cls: "bg-green-100 text-green-700",     Icon: Eye },
  clicked:   { label: "Clicked",   cls: "bg-emerald-100 text-emerald-800", Icon: MousePointerClick },
  bounced:   { label: "Bounced",   cls: "bg-red-100 text-red-700",         Icon: AlertCircle },
  dropped:   { label: "Dropped",   cls: "bg-red-100 text-red-700",         Icon: AlertCircle },
  failed:    { label: "Failed",    cls: "bg-red-100 text-red-700",         Icon: AlertCircle },
};

// Email actions only — non-email actions (e.g. config_updated) have no read state
const EMAIL_ACTIONS = ["quote_sent", "contract_sent", "rental_sent"];

const FAILED_STATUSES = ["bounced", "dropped", "failed"];

// Auto-refresh interval so opens appear without clicking Refresh
const POLL_MS = 30000;

const fmt = (d) => (d ? new Date(d).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "—");

const fmtSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (!n) return "";
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Pull "Q-123" out of "quotation-Q-123.pdf" for a friendlier label
const quoteRefFromFile = (file) => {
  if (!file) return null;
  const m = String(file).match(/Q-\d+/i);
  return m ? m[0].toUpperCase() : null;
};

const ReadCell = ({ log }) => {
  if (!EMAIL_ACTIONS.includes(log.action)) {
    return <span className="text-gray-400">—</span>;
  }

  if (FAILED_STATUSES.includes(log.status)) {
    return <span className="text-xs text-red-600">Not delivered</span>;
  }

  const opened = !!log.first_opened_at;
  const count = Number(log.open_count) || 0;
  const machine = !!log.meta?.machine_open;

  if (!opened) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
          <EyeOff className="w-3.5 h-3.5" /> Not read yet
        </span>
        {log.delivered_at && (
          <span className="text-[11px] text-gray-400">Delivered {fmt(log.delivered_at)}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <span
        className="inline-flex items-center gap-1 text-xs font-semibold text-green-700"
        title={machine ? "May include automatic opens by Apple Mail privacy protection" : ""}
      >
        <Eye className="w-3.5 h-3.5" /> Read {fmt(log.first_opened_at)}
        {machine && <span className="text-amber-600">*</span>}
      </span>
      <span className="text-[11px] text-gray-500">
        Opened {count} {count === 1 ? "time" : "times"}
        {count > 1 && log.last_opened_at ? ` · last ${fmt(log.last_opened_at)}` : ""}
      </span>
      {log.clicked_at && (
        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
          <MousePointerClick className="w-3 h-3" /> Clicked {fmt(log.clicked_at)}
        </span>
      )}
    </div>
  );
};

const DocumentCell = ({ log, onPreview }) => {
  if (!EMAIL_ACTIONS.includes(log.action)) {
    return <span className="text-gray-400">—</span>;
  }

  const url = log.meta?.file_url;
  const file = log.meta?.file;
  const ref = quoteRefFromFile(file);

  if (!url) {
    return (
      <div className="flex flex-col gap-0.5">
        {file && <span className="text-xs text-gray-600 break-all">{file}</span>}
        <span className="text-[11px] text-gray-400">Copy not stored</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-800">
        <FileText className="w-3.5 h-3.5 text-blue-600" />
        {ref || file || "Document"}
        {log.meta?.file_size ? (
          <span className="text-[11px] font-normal text-gray-400">· {fmtSize(log.meta.file_size)}</span>
        ) : null}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPreview(log)}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100"
        >
          <Eye className="w-3 h-3" /> View
        </button>
        <a
          href={url}
          download={file || true}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
        >
          <Download className="w-3 h-3" /> Download
        </a>
      </div>
    </div>
  );
};

const PreviewModal = ({ log, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!log) return null;
  const url = log.meta?.file_url;
  const file = log.meta?.file || "document.pdf";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div
        className="flex flex-col w-full max-w-5xl h-[90vh] bg-white rounded-lg shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-4 border-b border-gray-200">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate">
              {ACTION_LABELS[log.action] || log.action} — {quoteRefFromFile(file) || file}
            </h3>
            <p className="text-xs text-gray-500 truncate">
              Sent {fmt(log.created_at)}
              {log.user_name ? ` by ${log.user_name}` : ""}
              {log.recipient_email ? ` to ${log.recipient_email}` : ""}
            </p>
            {log.subject && <p className="text-xs text-gray-500 truncate">Subject: {log.subject}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <ExternalLink className="w-4 h-4" /> Open
            </a>
            <a
              href={url}
              download={file}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              <Download className="w-4 h-4" /> Download
            </a>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-gray-500 rounded-md hover:bg-gray-100"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <iframe title={file} src={url} className="flex-1 w-full bg-gray-100" />
      </div>
    </div>
  );
};

const LogsTab = ({ selectedConnectionId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewLog, setPreviewLog] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!selectedConnectionId) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      // cache: "no-store" + timestamp → always fetch fresh status (never a cached "delivered" row)
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/quote-logs/${selectedConnectionId}?_=${Date.now()}`,
        {
          cache: "no-store",
          headers: {
            Accept: "application/json",
            "Cache-Control": "no-cache",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setLogs(json.data || []);
    } catch (e) {
      console.error("Failed to load logs:", e);
      if (!silent) setError("Failed to load activity logs.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedConnectionId]);

  useEffect(() => { load(); }, [load]);

  // Poll quietly while the tab is visible so read status updates on its own
  useEffect(() => {
    if (!selectedConnectionId) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load(true);
    }, POLL_MS);
    return () => clearInterval(id);
  }, [load, selectedConnectionId]);

  const hasMachineOpens = logs.some((l) => l.meta?.machine_open);

  return (
    <div className="mt-4 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Activity Log</h3>
          <p className="text-xs text-gray-500">Open tracking is indicative — some email clients block or pre-load tracking images.</p>
        </div>
        <button onClick={() => load()} disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {error && <div className="p-4 text-sm text-red-700 bg-red-50">{error}</div>}

      {!error && logs.length === 0 && !loading && (
        <div className="p-8 text-center text-sm text-gray-500">No activity recorded yet.</div>
      )}

      {logs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">By</th>
                <th className="px-4 py-3">Recipient</th>
                <th className="px-4 py-3">Document</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Read</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map((log) => {
                const s = STATUS_STYLES[log.status] || STATUS_STYLES.queued;
                return (
                  <tr key={log.id} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{fmt(log.created_at)}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{ACTION_LABELS[log.action] || log.action}</td>
                    <td className="px-4 py-3 text-gray-700">{log.user_name || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">{log.recipient_email || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <DocumentCell log={log} onPreview={setPreviewLog} />
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${s.cls}`}
                        title={log.failure_reason || ""}>
                        <s.Icon className="w-3.5 h-3.5" /> {s.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <ReadCell log={log} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasMachineOpens && (
        <p className="px-4 py-2 text-[11px] text-gray-500 border-t border-gray-100">
          <span className="text-amber-600">*</span> May include automatic opens by Apple Mail privacy protection.
        </p>
      )}

      {previewLog && <PreviewModal log={previewLog} onClose={() => setPreviewLog(null)} />}
    </div>
  );
};

export default LogsTab;